import React from "react";
import {
  Box,
  Chip,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs from "dayjs";

import { calculateTimeDifference, getTwoDigitNo } from "utils";

import ScoreGauge from "./ScoreGauge";
import ScoreRing from "./ScoreRing";

/**
 * First feedback component (mobile) — mobile equivalent of desktop's
 * `PerformanceReview/OverallPerformance`.
 *
 * Header (assessment name + attempt chip + type/time/date) → Overall Score gauge
 * → Speech and Content ring cards. All values come from the shared feedback
 * store via the parent; timing/attempt-number come from the attempts list.
 *
 * @param {object}  template
 * @param {object}  attempt      current attempt (startedAt/finishedAt/createdAt/attemptNumber)
 * @param {number}  score        overall (avg of speech + content)
 * @param {number}  speech
 * @param {number}  content
 * @param {boolean} loading      feedback still loading
 * @param {boolean} attemptLoading attempts list still loading (drives the chip)
 * @param {Array}   allAttempts  finished attempts for this template (newest-first)
 * @param {string}  currentAttemptId  attempt currently being viewed
 * @param {Function} onSelectAttempt  called with the picked attempt's id
 */
export default function OverallScore({
  template,
  attempt,
  score,
  speech,
  content,
  loading,
  attemptLoading,
  allAttempts = [],
  currentAttemptId,
  onSelectAttempt = () => {},
}) {
  const timeTaken = React.useMemo(() => {
    if (!attempt?.startedAt || !attempt?.finishedAt) return "—";
    const { minutes, seconds } = calculateTimeDifference(
      attempt.startedAt,
      attempt.finishedAt
    );
    return minutes < 1
      ? `${getTwoDigitNo(seconds)} secs`
      : `${getTwoDigitNo(minutes)}:${getTwoDigitNo(seconds)} mins`;
  }, [attempt]);

  const attemptedOn = attempt?.createdAt
    ? dayjs(attempt.createdAt).format("DD/MM/YYYY")
    : "—";

  const attemptLabel =
    attempt?.attemptNumber != null
      ? `Attempt - ${getTwoDigitNo(attempt.attemptNumber)}`
      : "—";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Header */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 3,
          }}
        >
          {loading ? (
            <Skeleton variant="rectangular" width="60%" height={24} />
          ) : (
            <Typography variant="h6-medium" color="neutral.clr-800">
              Assessment:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {template?.name}
              </Box>
            </Typography>
          )}

          {attemptLoading ? (
            <Skeleton variant="rectangular" width={84} height={28} sx={{ borderRadius: 1, flexShrink: 0 }} />
          ) : allAttempts.length > 1 ? (
            // Attempt selector — mobile equivalent of desktop's "Select attempt"
            // SimpleSelect. The page is URL-driven, so picking an attempt
            // navigates to its feedback route (handled by onSelectAttempt).
            <Select
              size="small"
              value={currentAttemptId || ""}
              onChange={(e) => onSelectAttempt(e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
              renderValue={() => (
                <Typography variant="body02-semiBold" color="neutral.clr-700">
                  {attemptLabel}
                </Typography>
              )}
              sx={{
                flexShrink: 0,
                height: 30,
                bgcolor: "neutral.clr-100",
                borderRadius: 1,
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-select": {
                  py: 0,
                  pl: 2,
                  pr: "26px !important",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiSelect-icon": { color: "neutral.clr-600" },
              }}
            >
              {allAttempts.map((a) => (
                <MenuItem key={a._id} value={a._id}>
                  <Typography variant="body02-medium" color="neutral.clr-900">
                    Attempt - {getTwoDigitNo(a.attemptNumber)}
                    {a.createdAt
                      ? ` · ${dayjs(a.createdAt).format("DD/MM/YYYY")}`
                      : ""}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Chip
              label={attemptLabel}
              size="small"
              sx={{
                flexShrink: 0,
                bgcolor: "neutral.clr-100",
                color: "neutral.clr-700",
                borderRadius: 1,
                "& .MuiChip-label": { px: 2, fontWeight: 600 },
              }}
            />
          )}
        </Box>

        {loading ? (
          <Skeleton variant="rectangular" width="80%" height={16} />
        ) : (
          <>
            <Typography variant="body01-semiBold" color="neutral.clr-900">
              Type:{" "}
              <Box component="span" sx={{ textTransform: "capitalize" }}>
                {template?.type}
              </Box>
            </Typography>
            <Typography variant="body02-regular" color="neutral.clr-500">
              Time Taken: {timeTaken} | Attempted on: {attemptedOn}
            </Typography>
          </>
        )}
      </Box>

      {/* Overall Score gauge */}
      <Box
        sx={{
          bgcolor: "shades.clr-white-900",
          border: "1px solid",
          borderColor: "neutral.clr-100",
          borderRadius: 3,
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6-semiBold" color="neutral.clr-800">
            Overall Score
          </Typography>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "neutral.clr-400" }} />
        </Box>

        {loading ? (
          <Skeleton variant="rectangular" width={220} height={130} />
        ) : (
          <ScoreGauge value={score} breakpoint1={50} breakpoint2={70} />
        )}
      </Box>

      {/* Parameter rings */}
      {loading ? (
        <>
          <RingSkeleton />
          <RingSkeleton />
        </>
      ) : (
        <>
          <ScoreRing title="Speech Score" value={speech} category="speech" />
          <ScoreRing title="Content Score" value={content} category="content" />
        </>
      )}
    </Box>
  );
}

function RingSkeleton() {
  return (
    <Box
      sx={{
        bgcolor: "shades.clr-white-900",
        border: "1px solid",
        borderColor: "neutral.clr-100",
        borderRadius: 3,
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      <Skeleton variant="rectangular" width={120} height={20} />
      <Skeleton variant="circular" width={96} height={96} />
      <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1 }} />
    </Box>
  );
}
